// Appels de facturation depuis l'app (create-checkout-session / customer-portal-session).
//
// supabase-js range toute réponse non-2xx dans un FunctionsHttpError avec `data = null` : les
// tests `data?.error === 'not-authorized-for-billing'` des écrans ne voyaient donc JAMAIS le
// code métier (403, 409, 400 nothing-to-settle…) et affichaient « Edge Function returned a
// non-2xx status code ». On relit ici le corps JSON de l'erreur.
import { supabase } from './supabaseClient';
import {
  PENDING_PAID_ACTION_KEY, parsePendingPaidAction, type PendingPaidAction,
} from '../utils/checkoutReturn';

export interface BillingResult {
  data: Record<string, unknown> | null;
  /** Code métier (`body.error`) ou message technique ; null si succès. */
  code: string | null;
}

export async function invokeBilling(
  fn: 'create-checkout-session' | 'customer-portal-session',
  body: Record<string, unknown>,
): Promise<BillingResult> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (!error) {
    const d = (data ?? null) as Record<string, unknown> | null;
    return { data: d, code: typeof d?.error === 'string' ? d.error : null };
  }
  try {
    const ctx = (error as { context?: Response }).context;
    const parsed = ctx && typeof ctx.json === 'function' ? await ctx.json() : null;
    if (parsed && typeof parsed === 'object') {
      const d = parsed as Record<string, unknown>;
      return { data: d, code: typeof d.error === 'string' ? d.error : (error.message || 'error') };
    }
  } catch { /* corps illisible : on garde le message */ }
  return { data: null, code: error.message || 'error' };
}

// ── Action payée en attente (sessionStorage : survit à l'aller-retour Stripe dans l'onglet) ──
export const pendingPaidAction = {
  save(action: Omit<PendingPaidAction, 'createdAt' | 'confirmed'>): void {
    try { sessionStorage.setItem(PENDING_PAID_ACTION_KEY, JSON.stringify({ ...action, createdAt: Date.now() })); } catch { /* stockage indisponible */ }
  },
  read(): PendingPaidAction | null {
    try { return parsePendingPaidAction(sessionStorage.getItem(PENDING_PAID_ACTION_KEY), Date.now()); } catch { return null; }
  },
  markConfirmed(): void {
    const cur = pendingPaidAction.read();
    if (!cur) return;
    try { sessionStorage.setItem(PENDING_PAID_ACTION_KEY, JSON.stringify({ ...cur, confirmed: true })); } catch { /* ignore */ }
  },
  clear(): void {
    try { sessionStorage.removeItem(PENDING_PAID_ACTION_KEY); } catch { /* ignore */ }
  },
};
